import type { LoadedSample, SampleLibrary } from './SampleLibrary';
// Vite ships the worklet as a static asset — the ?url import gives us a
// URL string that ctx.audioWorklet.addModule can load at runtime.
import stutterWorkletUrl from '../worklets/stutter-processor.js?url';
import { concatFloat32, encodeWav } from '../utils/wavEncoder';

type ActiveVoice = {
  sampleId: string;
  source: AudioBufferSourceNode;
  gain: GainNode;
};

export class AudioSystem {
  private context: AudioContext | null = null;
  private unlocked = false;
  private master: GainNode | null = null;
  private baseMasterGain = 0.9;
  private muted = false;
  private masterLimiter: DynamicsCompressorNode | null = null;
  /**
   * Song bus: sequencer notes + armed loop bricks route here. Goes to speakers
   * (via master) AND to the recorder — so the recording only contains the song,
   * not brick-hit blips / wall bounces / paddle taps / jingles.
   */
  private songBus: GainNode | null = null;
  /**
   * Brick-hit bus: transient blips fired when the ball hits a brick route here.
   * Runs through the same NOIZ FX as songBus so they inherit the same character,
   * but is a separate node so metronome / UI blips can stay dry on the master.
   */
  private brickBus: GainNode | null = null;
  // ---- Real-time NOIZ FX chain (applies to song + brick hits) ----
  // (songBus + brickBus) → fxIn → Bit → Reverb → Stutter → OTT → master
  // recordDest taps fxOut so the recording captures song + brick hits with FX applied.
  private fxIn: GainNode | null = null;
  private fxOut: GainNode | null = null;
  // Stutter (replaces Crunch): short tempo-synced delay + high feedback,
  // crossfaded against dry so cranking the slider fills the signal with
  // repeat-artifacts rather than clipping distortion. The slot keeps the
  // "crunch" param key upstream so the UI wiring / settings stay unchanged.
  private crunchDry: GainNode | null = null;
  private crunchWet: GainNode | null = null;
  private crunchOut: GainNode | null = null;
  private stutterNode: AudioWorkletNode | null = null;
  private stutterReady = false;
  // Reverb: ConvolverNode with a procedurally generated decaying-noise
  // impulse response, crossfaded against dry. Replaces the tempo-synced
  // delay slot — echo/repeat behavior now lives inside the stutter
  // worklet's "delay-mode" cycles, so this slider is free to add space.
  private reverbNode: ConvolverNode | null = null;
  private reverbDry: GainNode | null = null;
  private reverbWet: GainNode | null = null;
  // OTT — 3-band multiband compressor sitting last in the chain. Each band
  // gets pre-gain (+12dB) so quiet material lifts into the compressor's
  // threshold zone, then aggressive downward comp glues everything together;
  // dry/wet crossfade drives the slider. Approximates Xfer OTT's upward+downward
  // multiband feel with only DynamicsCompressorNode primitives.
  private ottDry: GainNode | null = null;
  private ottWet: GainNode | null = null;
  private ottMakeup: GainNode | null = null;
  // Bitcrusher — WaveShaper with a quantized transfer curve. Dry+wet mix.
  private bitShaper: WaveShaperNode | null = null;
  private bitDry: GainNode | null = null;
  private bitWet: GainNode | null = null;
  private bitOut: GainNode | null = null;
  // MediaStreamDestination is kept for the video recorder (which needs a
  // MediaStream track to mux with the canvas), but the audio-only export
  // path taps fxOut directly as PCM so we can emit a DAW-friendly WAV.
  // MediaRecorder's webm/opus output is rejected by most DAWs on import.
  private recordDest: MediaStreamAudioDestinationNode | null = null;
  private recordProcessor: ScriptProcessorNode | null = null;
  private recordSilentSink: GainNode | null = null;
  private recordChannels: Float32Array[][] = [];
  private recordSampleRate = 44100;
  private recordActive = false;
  /** Sustained free-running loops (long pads), keyed by brick/slot. */
  private readonly loops = new Map<string, ActiveVoice>();
  /** One-shot / beat voices that can be cut on re-trigger, keyed by slot. */
  private readonly voices = new Map<string, ActiveVoice>();
  private readonly oneShotGain = 0.85;
  private readonly loopGain = 0.42;
  private readonly beatVoiceGain = 0.78;

  constructor(private readonly library: SampleLibrary) {
    const unlock = () => {
      void this.unlock();
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
  }

  async unlock(): Promise<void> {
    // Fire-and-forget resume; browsers only actually run it after a real user gesture,
    // but decodeAudioData works on a suspended context so bootstrap can proceed regardless.
    if (this.unlocked && this.context) {
      if (this.context.state !== 'running') void this.context.resume();
      return;
    }

    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;

    this.context = new AudioContextClass();
    this.master = this.context.createGain();
    this.master.gain.value = this.muted ? 0 : this.baseMasterGain;
    // Invisible brickwall-ish limiter on the master out so peaks never clip when
    // multiple voices stack (esp. supersaw / reverb tails / reese bass). Threshold
    // just below 0 dBFS, hard ratio, near-zero attack.
    this.masterLimiter = this.context.createDynamicsCompressor();
    this.masterLimiter.threshold.value = -1;
    this.masterLimiter.knee.value = 0;
    this.masterLimiter.ratio.value = 20;
    this.masterLimiter.attack.value = 0.003;
    this.masterLimiter.release.value = 0.12;
    this.master.connect(this.masterLimiter);
    this.masterLimiter.connect(this.context.destination);
    this.songBus = this.context.createGain();
    this.songBus.gain.value = 1.0;
    this.brickBus = this.context.createGain();
    this.brickBus.gain.value = 1.0;
    // Load the stutter worklet before building the FX graph so buildFxChain
    // can drop the AudioWorkletNode straight into the stutter slot. On
    // failure we log + fall back to a passthrough gain so the app still runs.
    try {
      await this.context.audioWorklet.addModule(stutterWorkletUrl);
      this.stutterReady = true;
    } catch (err) {
      console.warn('Stutter worklet failed to load', err);
      this.stutterReady = false;
    }
    this.buildFxChain(this.context);
    // Both song + brick voices route through the FX chain.
    this.songBus.connect(this.fxIn!);
    this.brickBus.connect(this.fxIn!);
    this.library.setContext(this.context);
    void this.context.resume();
    this.unlocked = true;
    await this.library.decodePending();
  }

  getContext(): AudioContext | null {
    return this.context;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.master && this.context) {
      this.master.gain.setTargetAtTime(
        muted ? 0 : this.baseMasterGain,
        this.context.currentTime,
        0.01,
      );
    }
  }

  isMuted(): boolean {
    return this.muted;
  }

  /** Set master output level (0..1). Slider-driven; respects current mute
   *  state (muted overrides to 0 and the new base takes effect on unmute). */
  setMasterVolume(volume: number): void {
    const clamped = Math.min(1, Math.max(0, volume));
    this.baseMasterGain = clamped;
    if (this.master && this.context && !this.muted) {
      this.master.gain.setTargetAtTime(clamped, this.context.currentTime, 0.01);
    }
  }

  getMasterVolume(): number {
    return this.baseMasterGain;
  }

  /** Wire the real-time NOIZ FX chain (order: Bit → Reverb → Stutter → OTT
   *  → master). OTT lives last so it acts as a multiband finisher/glue on
   *  everything above. Chorus was pulled to make room; the slider was rewired
   *  to OTT wet/dry. */
  private buildFxChain(ctx: AudioContext): void {
    const master = this.master!;

    // fxIn is the entry — songBus connects here.
    this.fxIn = ctx.createGain();

    // 1) Bit — WaveShaper with quantized transfer curve (steps 256→6 as the
    //    slider climbs).
    this.bitShaper = ctx.createWaveShaper();
    this.bitShaper.curve = this.buildBitCrushCurve(256);
    this.bitShaper.oversample = '2x';
    this.bitDry = ctx.createGain();
    this.bitDry.gain.value = 1;
    this.bitWet = ctx.createGain();
    this.bitWet.gain.value = 0;
    this.bitOut = ctx.createGain();
    this.fxIn.connect(this.bitDry);
    this.bitDry.connect(this.bitOut);
    this.fxIn.connect(this.bitShaper);
    this.bitShaper.connect(this.bitWet);
    this.bitWet.connect(this.bitOut);

    // 2) Reverb — ConvolverNode with a procedurally generated decaying-noise
    //    impulse response, dry+wet mix into `crunchOut` (retained bus name).
    this.reverbNode = ctx.createConvolver();
    this.reverbNode.buffer = this.buildReverbImpulse(ctx);
    this.reverbDry = ctx.createGain();
    this.reverbDry.gain.value = 1;
    this.reverbWet = ctx.createGain();
    this.reverbWet.gain.value = 0;
    this.crunchOut = ctx.createGain(); // post-reverb bus
    this.bitOut.connect(this.reverbDry);
    this.bitOut.connect(this.reverbNode);
    this.reverbNode.connect(this.reverbWet);
    this.reverbDry.connect(this.crunchOut);
    this.reverbWet.connect(this.crunchOut);

    // 3) Stutter — AudioWorkletNode beat-repeat. Dry+wet crossfade. Names
    //    `crunchDry`/`crunchWet` kept for backward compat with the UI slider
    //    key `crunch` (see setNoizFx callers). `fxOut` is the post-stutter
    //    bus and feeds the OTT finisher below.
    this.crunchDry = ctx.createGain();
    this.crunchDry.gain.value = 1;
    this.crunchWet = ctx.createGain();
    this.crunchWet.gain.value = 0;
    this.fxOut = ctx.createGain();
    this.crunchOut.connect(this.crunchDry);
    this.crunchDry.connect(this.fxOut);
    if (this.stutterReady) {
      try {
        this.stutterNode = new AudioWorkletNode(ctx, 'stutter-processor', {
          numberOfInputs: 1,
          numberOfOutputs: 1,
          channelCount: 1,
          channelCountMode: 'explicit',
        });
        const amountParam = this.stutterNode.parameters.get('amount');
        if (amountParam) amountParam.value = 0;
        this.crunchOut.connect(this.stutterNode);
        this.stutterNode.connect(this.crunchWet);
        this.crunchWet.connect(this.fxOut);
      } catch (err) {
        console.warn('Stutter node construction failed', err);
        this.stutterNode = null;
        this.crunchWet.connect(this.fxOut);
      }
    } else {
      this.crunchWet.connect(this.fxOut);
    }

    // 4) OTT — 3-band multiband compression finisher. Per band: pre-gain
    //    (+12dB) so quiet material lifts into the compressor's threshold zone,
    //    aggressive downward comp, then post-gain to normalize. The three band
    //    outs sum into `ottMakeup`, which crossfades against dry via ottDry/Wet.
    this.ottDry = ctx.createGain();
    this.ottDry.gain.value = 1;
    this.ottWet = ctx.createGain();
    this.ottWet.gain.value = 0;
    this.ottMakeup = ctx.createGain();
    this.ottMakeup.gain.value = 0.7; // trim so full-wet doesn't outpace dry
    const ottSum = ctx.createGain();

    const buildBand = (filters: BiquadFilterNode[]) => {
      const pre = ctx.createGain();
      pre.gain.value = 4; // ~+12dB pre-gain, pushes quiet stuff into threshold
      const comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -30;
      comp.knee.value = 6;
      comp.ratio.value = 6;
      comp.attack.value = 0.003;
      comp.release.value = 0.15;
      const post = ctx.createGain();
      post.gain.value = 0.35; // undo pre-gain, leave a hair of lift
      this.fxOut!.connect(filters[0]!);
      let tail: AudioNode = filters[0]!;
      for (let i = 1; i < filters.length; i++) {
        tail.connect(filters[i]!);
        tail = filters[i]!;
      }
      tail.connect(pre);
      pre.connect(comp);
      comp.connect(post);
      post.connect(ottSum);
    };

    const lowFilter = ctx.createBiquadFilter();
    lowFilter.type = 'lowpass';
    lowFilter.frequency.value = 200;
    buildBand([lowFilter]);

    const midHP = ctx.createBiquadFilter();
    midHP.type = 'highpass';
    midHP.frequency.value = 200;
    const midLP = ctx.createBiquadFilter();
    midLP.type = 'lowpass';
    midLP.frequency.value = 2500;
    buildBand([midHP, midLP]);

    const highFilter = ctx.createBiquadFilter();
    highFilter.type = 'highpass';
    highFilter.frequency.value = 2500;
    buildBand([highFilter]);

    ottSum.connect(this.ottMakeup);
    this.ottMakeup.connect(this.ottWet);
    this.fxOut.connect(this.ottDry);
    this.ottDry.connect(master);
    this.ottWet.connect(master);
  }

  /** Procedural reverb impulse — a stereo decaying-noise buffer that reads as
   *  a small hall / plate when convolved. Cheap and deterministic; avoids
   *  shipping an IR audio file. */
  private buildReverbImpulse(ctx: BaseAudioContext): AudioBuffer {
    const sr = ctx.sampleRate;
    const seconds = 1.6;
    const length = Math.floor(sr * seconds);
    const buf = ctx.createBuffer(2, length, sr);
    for (let ch = 0; ch < 2; ch++) {
      const data = buf.getChannelData(ch);
      for (let i = 0; i < length; i++) {
        // Exponential decay: near-instant peak, ~1.6s tail.
        const t = i / length;
        const decay = Math.pow(1 - t, 2.6);
        data[i] = (Math.random() * 2 - 1) * decay;
      }
    }
    return buf;
  }

  /** Build a WaveShaper transfer curve that quantizes input to `steps` levels
   *  — bit-crush approximation. Higher steps = cleaner, fewer = more digital
   *  grit. Curve is symmetric around 0. */
  private buildBitCrushCurve(steps: number): Float32Array<ArrayBuffer> {
    const n = 4096;
    // Explicit ArrayBuffer backing so the return type matches
    // WaveShaperNode.curve's `Float32Array<ArrayBuffer>` requirement in
    // current TS lib (bare `new Float32Array(n)` is
    // `Float32Array<ArrayBufferLike>`, which doesn't satisfy it).
    const curve = new Float32Array(new ArrayBuffer(n * 4));
    for (let i = 0; i < n; i++) {
      const x = (i / (n - 1)) * 2 - 1; // -1..+1
      curve[i] = Math.round(x * steps) / steps;
    }
    return curve;
  }

/** Set NOIZ FX slider values (0..1). Any-slider-any-time; applies immediately. */
  setNoizFx(
    params: Partial<{
      crunch: number;
      delay: number;
      ott: number;
      bit: number;
    }>,
  ): void {
    if (!this.context) return;
    const now = this.context.currentTime;
    if (params.crunch !== undefined && this.crunchDry && this.crunchWet) {
      this.crunchDry.gain.setTargetAtTime(1 - params.crunch, now, 0.01);
      this.crunchWet.gain.setTargetAtTime(params.crunch, now, 0.01);
      // Same value drives the worklet's internal aggressiveness (chunk size
      // shrinks + repeats grow as amount rises).
      const amt = this.stutterNode?.parameters.get('amount');
      if (amt) amt.setTargetAtTime(params.crunch, now, 0.01);
    }
    if (params.delay !== undefined && this.reverbDry && this.reverbWet) {
      // Slider name kept as `delay` for backward compat with UI + sync code
      // (see setNoizFx callers). Behavior: now drives reverb wet/dry — echo
      // repeats live inside the stutter worklet's delay-mode cycles.
      this.reverbDry.gain.setTargetAtTime(1 - params.delay * 0.35, now, 0.01);
      this.reverbWet.gain.setTargetAtTime(params.delay * 0.9, now, 0.01);
    }
    if (params.ott !== undefined && this.ottDry && this.ottWet) {
      // Full crossfade — at slider=1 the entire signal runs through the
      // multiband OTT bus. Dry side rolls off slower so partial slider
      // positions still keep body from the pre-OTT signal.
      this.ottDry.gain.setTargetAtTime(1 - params.ott * 0.6, now, 0.01);
      this.ottWet.gain.setTargetAtTime(params.ott, now, 0.01);
    }
    if (params.bit !== undefined && this.bitDry && this.bitWet && this.bitShaper) {
      // Wet/dry mix + curve step count both track the slider. Steps: 256 → 6.
      this.bitDry.gain.setTargetAtTime(1 - params.bit, now, 0.01);
      this.bitWet.gain.setTargetAtTime(params.bit, now, 0.01);
      const steps = Math.max(6, Math.round(256 * (1 - params.bit) + 6 * params.bit));
      this.bitShaper.curve = this.buildBitCrushCurve(steps);
    }
  }

  /** AudioContext time for "now", or performance fallback. */
  now(): number {
    return this.context?.currentTime ?? 0;
  }

  async decode(data: ArrayBuffer): Promise<AudioBuffer> {
    await this.unlock();
    if (!this.context) throw new Error('AudioContext unavailable');
    return this.context.decodeAudioData(data.slice(0));
  }

  activeLoopCount(): number {
    return this.loops.size;
  }

  isLooping(key: string): boolean {
    return this.loops.has(key);
  }

  /**
   * Fire a one-shot at an absolute AudioContext time (beat-quantized).
   * `bus` selects the routing:
   *   - 'fx'   → master only (brick hit blips, not recorded)
   *   - 'song' → song bus (sequencer notes, recorded)
   *
   * `voiceKey` (optional) enforces monophony: a new trigger with the same key
   * cuts the previous voice (regardless of bus). Pass `sample.id` to keep the
   * same sample from stacking on itself and doubling in volume.
   */
  playOneShotAt(
    sample: LoadedSample,
    when: number,
    playbackRate = 1,
    gainScale = 1,
    bus: 'fx' | 'song' = 'fx',
    voiceKey?: string,
    holdSeconds?: number,
  ): void {
    // fx voices route through brickBus so brick-hit blips get the NOIZ FX too.
    const dest = bus === 'song' ? this.songBus : this.brickBus;
    if (!this.context || !dest || !sample.buffer) return;
    if (voiceKey) this.stopVoice(voiceKey, when);
    const source = this.context.createBufferSource();
    const gain = this.context.createGain();
    source.buffer = sample.buffer;
    source.playbackRate.value = playbackRate;
    const start = Math.max(when, this.context.currentTime);
    gain.gain.setValueAtTime(this.oneShotGain * gainScale, start);
    source.connect(gain).connect(dest);
    source.start(start);
    if (holdSeconds !== undefined && holdSeconds > 0) {
      // Cut the note at exactly `holdSeconds` so consecutive triggers don't pile up.
      const stopAt = start + holdSeconds;
      const release = Math.min(0.02, holdSeconds * 0.25);
      try {
        gain.gain.setValueAtTime(this.oneShotGain * gainScale, Math.max(start, stopAt - release));
        gain.gain.exponentialRampToValueAtTime(0.0001, stopAt);
        source.stop(stopAt + 0.02);
      } catch {
        // scheduling failed; ignore
      }
    }
    if (voiceKey) {
      const key = voiceKey;
      this.voices.set(key, { sampleId: sample.id, source, gain });
      source.onended = () => {
        if (this.voices.get(key)?.source === source) this.voices.delete(key);
      };
    }
  }

  /**
   * Percussive synth fallback for when no sample is loaded (or buffer missing).
   * Uses a short pitched blip so hits are always audible on first play.
   */
  hitBlipAt(when: number, seed = 0, chaos = 0): void {
    if (!this.context || !this.master) return;
    const start = Math.max(when, this.context.currentTime);
    // Pitch pool spans a wide range so brick columns feel distinct.
    const scale = [110, 138.6, 164.8, 220, 261.6, 329.6, 392, 523.2];
    const base = scale[((seed % scale.length) + scale.length) % scale.length]!;
    const detune = 1 + (Math.random() * 2 - 1) * (0.02 + chaos * 0.4);
    const freq = base * detune;
    const osc = this.context.createOscillator();
    const gain = this.context.createGain();
    osc.type = chaos > 0.4 ? 'square' : 'triangle';
    osc.frequency.setValueAtTime(freq * 1.6, start);
    osc.frequency.exponentialRampToValueAtTime(freq, start + 0.02);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.22, start + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.18);
    osc.connect(gain).connect(this.master);
    osc.start(start);
    osc.stop(start + 0.22);
  }

  /**
   * Beat-synced voice for loop bricks: re-triggers on each beat, mono per key.
   * Cuts the previous voice for that key so dense tempos stay clean.
   */
  playBeatVoiceAt(key: string, sample: LoadedSample, when: number, playbackRate = 1): void {
    if (!this.context || !this.songBus || !sample.buffer) return;
    this.stopVoice(key, when);

    const source = this.context.createBufferSource();
    const gain = this.context.createGain();
    source.buffer = sample.buffer;
    source.playbackRate.value = playbackRate;
    const start = Math.max(when, this.context.currentTime);
    gain.gain.setValueAtTime(this.beatVoiceGain, start);
    source.connect(gain).connect(this.songBus);
    source.start(start);
    this.voices.set(key, { sampleId: sample.id, source, gain });
    source.onended = () => {
      if (this.voices.get(key)?.source === source) this.voices.delete(key);
    };
  }

  /** Long free-running loop, started on a beat. */
  startLoopAt(key: string, sample: LoadedSample, when: number): void {
    if (!this.context || !this.songBus || !sample.buffer) return;
    this.stopLoopAt(key, when);

    const source = this.context.createBufferSource();
    const gain = this.context.createGain();
    source.buffer = sample.buffer;
    source.loop = true;
    const start = Math.max(when, this.context.currentTime);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(this.loopGain, start + 0.05);
    source.connect(gain).connect(this.songBus);
    source.start(start);
    this.loops.set(key, { sampleId: sample.id, source, gain });
  }

  stopLoopAt(key: string, when = this.now()): void {
    const active = this.loops.get(key);
    if (!active || !this.context) return;
    const t = Math.max(when, this.context.currentTime);
    try {
      active.gain.gain.cancelScheduledValues(t);
      active.gain.gain.setValueAtTime(Math.max(active.gain.gain.value, 0.0001), t);
      active.gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
      active.source.stop(t + 0.08);
    } catch {
      // already stopped
    }
    this.loops.delete(key);
  }

  isVoiceActive(key: string): boolean {
    return this.voices.has(key);
  }

  stopVoice(key: string, when = this.now()): void {
    const active = this.voices.get(key);
    if (!active || !this.context) return;
    const t = Math.max(when, this.context.currentTime);
    try {
      active.gain.gain.cancelScheduledValues(t);
      active.gain.gain.setValueAtTime(Math.max(active.gain.gain.value, 0.0001), t);
      active.gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.02);
      active.source.stop(t + 0.03);
    } catch {
      // already stopped
    }
    this.voices.delete(key);
  }

  stopAllLoops(when = this.now()): void {
    for (const key of [...this.loops.keys()]) this.stopLoopAt(key, when);
    for (const key of [...this.voices.keys()]) this.stopVoice(key, when);
  }

  /** Soft metronome click — optional downbeat feel. */
  beatClick(when: number, downbeat: boolean): void {
    if (!this.context || !this.master) return;
    const osc = this.context.createOscillator();
    const gain = this.context.createGain();
    const start = Math.max(when, this.context.currentTime);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(downbeat ? 880 : 660, start);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(downbeat ? 0.22 : 0.11, start + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.06);
    osc.connect(gain).connect(this.master);
    osc.start(start);
    osc.stop(start + 0.05);
  }

  wallHit(): void {
    this.blip(420, 0.04, 'square', 0.04);
  }

  paddleHit(): void {
    this.blip(180, 0.06, 'triangle', 0.06);
  }

  lifeLost(): void {
    this.blip(110, 0.28, 'sawtooth', 0.08);
  }

  win(): void {
    this.blip(523.25, 0.12, 'triangle', 0.08);
    window.setTimeout(() => this.blip(659.25, 0.12, 'triangle', 0.08), 90);
    window.setTimeout(() => this.blip(783.99, 0.18, 'triangle', 0.09), 180);
  }

  private blip(freq: number, duration: number, type: OscillatorType, gainValue: number): void {
    if (!this.context || !this.master) return;
    const osc = this.context.createOscillator();
    const gain = this.context.createGain();
    const now = this.context.currentTime;
    osc.type = type;
    osc.frequency.setValueAtTime(freq, now);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(gainValue, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    osc.connect(gain).connect(this.master);
    osc.start(now);
    osc.stop(now + duration + 0.02);
  }

  isRecording(): boolean {
    return this.recordActive;
  }

  /** Song bus as a MediaStream, so a video recorder can grab the audio track. */
  getSongStream(): MediaStream | null {
    if (!this.recordDest) return null;
    return this.recordDest.stream;
  }

  /** Preview an arbitrary AudioBuffer (used for the chop preview click). Returns a stop function. */
  playBuffer(
    buffer: AudioBuffer,
    bus: 'fx' | 'song' = 'fx',
    offsetSec = 0,
    gainScale = 1,
  ): () => void {
    // fx voices route through brickBus for FX; song voices via songBus.
    const dest = bus === 'song' ? this.songBus : this.brickBus;
    if (!this.context || !dest) return () => {};
    const source = this.context.createBufferSource();
    const gain = this.context.createGain();
    source.buffer = buffer;
    const start = this.context.currentTime;
    gain.gain.setValueAtTime(gainScale, start);
    source.connect(gain).connect(dest);
    source.start(start, Math.max(0, offsetSec));
    return () => {
      try {
        const t = this.context?.currentTime ?? 0;
        gain.gain.cancelScheduledValues(t);
        gain.gain.setValueAtTime(gain.gain.value, t);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.03);
        source.stop(t + 0.05);
      } catch {
        // already stopped
      }
    };
  }

  async startRecording(): Promise<boolean> {
    await this.unlock();
    if (!this.context || !this.master || !this.fxOut) return false;
    if (this.isRecording()) return true;
    // MediaStream destination stays around for the video recorder's audio
    // track — it needs a live MediaStream, not raw PCM. Audio-only export
    // reads Float32 straight off the ScriptProcessor below.
    if (!this.recordDest) {
      this.recordDest = this.context.createMediaStreamDestination();
      this.fxOut.connect(this.recordDest);
    }

    const ctx = this.context;
    const bufferSize = 4096;
    const numChannels = 2;
    const processor = ctx.createScriptProcessor(bufferSize, numChannels, numChannels);
    const channels: Float32Array[][] = Array.from({ length: numChannels }, () => []);
    processor.onaudioprocess = (ev) => {
      if (!this.recordActive) return;
      const input = ev.inputBuffer;
      const chCount = Math.min(numChannels, input.numberOfChannels);
      for (let ch = 0; ch < chCount; ch++) {
        // getChannelData returns a view onto the input buffer that the
        // browser reuses next callback — copy before stashing.
        channels[ch]!.push(new Float32Array(input.getChannelData(ch)));
      }
      // If input is mono, duplicate into the second channel so the WAV
      // stays stereo end-to-end.
      if (chCount < numChannels && chCount > 0) {
        for (let ch = chCount; ch < numChannels; ch++) {
          channels[ch]!.push(new Float32Array(input.getChannelData(0)));
        }
      }
    };
    // ScriptProcessorNode only fires when it has a path to context.destination.
    // Route through a silent gain so we don't double the audio to the speakers.
    const silent = ctx.createGain();
    silent.gain.value = 0;
    this.fxOut.connect(processor);
    processor.connect(silent);
    silent.connect(ctx.destination);

    this.recordProcessor = processor;
    this.recordSilentSink = silent;
    this.recordChannels = channels;
    this.recordSampleRate = ctx.sampleRate;
    this.recordActive = true;
    return true;
  }

  stopRecording(): Promise<Blob | null> {
    return new Promise((resolve) => {
      if (!this.recordActive || !this.recordProcessor) {
        resolve(null);
        return;
      }
      this.recordActive = false;
      const processor = this.recordProcessor;
      const silent = this.recordSilentSink;
      const channels = this.recordChannels;
      const sampleRate = this.recordSampleRate;
      try {
        this.fxOut?.disconnect(processor);
      } catch {
        // already disconnected
      }
      try {
        processor.disconnect();
      } catch {
        // ignore
      }
      try {
        silent?.disconnect();
      } catch {
        // ignore
      }
      processor.onaudioprocess = null;
      this.recordProcessor = null;
      this.recordSilentSink = null;
      this.recordChannels = [];
      const merged = channels.map((chunks) => concatFloat32(chunks));
      if (!merged[0] || merged[0].length === 0) {
        resolve(null);
        return;
      }
      resolve(encodeWav(merged, sampleRate));
    });
  }

  dispose(): void {
    this.stopAllLoops();
    if (this.isRecording()) void this.stopRecording();
    void this.context?.close();
    this.context = null;
    this.master = null;
    this.masterLimiter = null;
    this.songBus = null;
    this.brickBus = null;
    this.fxIn = null;
    this.fxOut = null;
    this.crunchDry = null;
    this.crunchWet = null;
    this.crunchOut = null;
    this.stutterNode = null;
    this.stutterReady = false;
    this.reverbNode = null;
    this.reverbDry = null;
    this.reverbWet = null;
    this.recordDest = null;
    this.recordProcessor = null;
    this.recordSilentSink = null;
    this.recordChannels = [];
    this.recordActive = false;
  }
}
